import { IconAlertTriangle, IconFlame } from "@tabler/icons-react";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

/** Must match each global strip row height (sidebar offset is a multiple of this). */
export const SITE_NOTICE_BANNER_HEIGHT = "1.875rem";

/** @deprecated Prefer SITE_NOTICE_BANNER_HEIGHT — kept for existing offset CSS var consumers. */
export const CAPACITY_BANNER_OFFSET = SITE_NOTICE_BANNER_HEIGHT;

export type SiteNoticeTone = "amber" | "destructive" | "sandbox";

const toneStyles: Record<
  SiteNoticeTone,
  {
    surface: string;
    border: string;
    detail: string;
    icon: string;
  }
> = {
  amber: {
    surface: "bg-amber-400 text-amber-950",
    border: "border-amber-500",
    detail: "text-amber-950/85",
    icon: "size-4 shrink-0 text-amber-950",
  },
  destructive: {
    surface: "bg-red-400 text-red-950",
    border: "border-red-500",
    detail: "text-red-950/85",
    icon: "size-4 shrink-0 text-red-950",
  },
  sandbox: {
    surface: "bg-[#533AFD] text-white",
    border: "border-[#533AFD]",
    detail: "text-white/85",
    icon: "size-4 shrink-0 text-white",
  },
};

export const siteNoticeToneSurfaceClassName = (tone: SiteNoticeTone) => toneStyles[tone].surface;
export const siteNoticeToneIconClassName = (tone: SiteNoticeTone) => toneStyles[tone].icon;

type SiteNoticeBannerProps = {
  tone: SiteNoticeTone;
  title: string;
  detail?: string;
  /** Optional icon override. Defaults to flame (amber) or alert triangle (destructive/sandbox). */
  icon?: ReactNode;
  className?: string;
};

export const SiteNoticeBanner = ({ tone, title, detail, icon, className }: SiteNoticeBannerProps) => {
  const styles = toneStyles[tone];
  const fullLabel = detail ? `${title} · ${detail}` : title;
  const DefaultIcon = tone === "amber" ? IconFlame : IconAlertTriangle;

  return (
    <div
      role="alert"
      className={cn(
        "relative z-30 flex w-full shrink-0 items-center justify-center gap-3 overflow-hidden border-b px-4 text-[0.8125rem]",
        styles.surface,
        styles.border,
        className,
      )}
      style={{ height: SITE_NOTICE_BANNER_HEIGHT }}
    >
      <span className={cn("inline-flex items-center justify-center [&_svg]:size-4", styles.icon)} aria-hidden>
        {icon ?? <DefaultIcon />}
      </span>
      <p
        className="min-w-0 max-w-full text-center text-balance leading-snug line-clamp-2 md:line-clamp-1 md:truncate"
        title={fullLabel}
      >
        <span className="font-medium">{title}</span>
        {detail ? <span className={styles.detail}> · {detail}</span> : null}
      </p>
    </div>
  );
};
